
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Clock, Calendar } from 'lucide-react';
import { TeacherAvailabilityException } from '@/hooks/useTeacherDetail';

interface TeacherAvailabilityExceptionsProps {
  exceptions: TeacherAvailabilityException[];
}

export const TeacherAvailabilityExceptions: React.FC<TeacherAvailabilityExceptionsProps> = ({
  exceptions,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    const time = new Date(`2000-01-01T${timeString}`);
    return time.toLocaleTimeString('th-TH', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const getExceptionTypeText = (type: string) => {
    switch (type) {
      case 'NO_WORK':
        return 'ไม่ทำงาน';
      case 'PARTIAL_UNAVAILABLE':
        return 'ไม่ว่างเฉพาะช่วง';
      default:
        return type;
    }
  };

  const getExceptionTypeColor = (type: string) => {
    switch (type) {
      case 'NO_WORK':
        return 'bg-red-100 text-red-800';
      case 'PARTIAL_UNAVAILABLE':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const activeExceptions = exceptions.filter(exception => exception.is_active);

  if (activeExceptions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            วันหยุดชั่วคราว
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-gray-500 py-8">ไม่มีข้อยกเว้นตารางเวลา</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          วันหยุดชั่วคราว ({activeExceptions.length} รายการ)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {activeExceptions.map((exception) => (
            <div key={exception.id} className="border rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">
                    {formatDate(exception.exception_date)}
                  </span>
                </div>
                <Badge className={getExceptionTypeColor(exception.exception_type)}>
                  {getExceptionTypeText(exception.exception_type)}
                </Badge>
              </div>
              
              {exception.start_time && exception.end_time && (
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <span className="text-sm">
                    {formatTime(exception.start_time)} - {formatTime(exception.end_time)}
                  </span>
                </div>
              )}
              
              {exception.reason && (
                <p className="text-sm text-gray-600 mt-2">
                  <strong>เหตุผล:</strong> {exception.reason}
                </p>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
